import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { MessageService } from "primeng/api";

@Injectable({
  providedIn: "root",
})
export class ToastService {
  constructor(private readonly messageService: MessageService) {}

  /**
   * Shows a success message
   * @param summary The summary of the message
   * @param detail The detail of the message
   * @param life The life of the message in milliseconds
   */
  public showSuccess(
    summary: string,
    detail?: string,
    life: number = 3000,
  ): void {
    this.messageService.add({
      severity: "success",
      summary,
      detail,
      life,
    });
  }

  /**
   * Shows an info message
   * @param summary The summary of the message
   * @param detail The detail of the message
   * @param life The life of the message in milliseconds
   */
  public showInfo(summary: string, detail?: string, life: number = 3000): void {
    this.messageService.add({
      severity: "info",
      summary,
      detail,
      life,
    });
  }

  /**
   * Shows a warning message
   * @param summary The summary of the message
   * @param detail The detail of the message
   * @param life The life of the message in milliseconds
   */
  public showWarning(
    summary: string,
    detail?: string,
    life: number = 3000,
  ): void {
    this.messageService.add({
      severity: "warn",
      summary,
      detail,
      life,
    });
  }

  /**
   * Shows an error message
   * @param summary The summary of the message
   * @param detail The detail of the message
   * @param life The life of the message in milliseconds
   */
  public showError(
    summary: string,
    detail?: string,
    life: number = 5000,
  ): void {
    this.messageService.add({
      severity: "error",
      summary,
      detail,
      life,
    });
  }

  /**
   * Shows an HTTP error message
   * @param error The HTTP error response
   * @param defaultMessage The default message to show if the error doesn't have a message
   */
  public showHttpError(
    error: HttpErrorResponse,
    defaultMessage: string = "An error occurred",
  ): void {
    let errorMessage: string = defaultMessage;
    let errorDetail: string = "";

    if (error) {
      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = "Client Error";
        errorDetail = error.error.message;
      } else if (error instanceof HttpErrorResponse) {
        // Server-side error
        errorMessage = `Error ${error.status}`;
        if (typeof error.error === "string") {
          errorDetail = error.error;
        } else if (
          typeof error.error === "object" &&
          error.error !== null &&
          Object.hasOwn(error.error as object, "message") &&
          typeof (error.error as { message?: unknown }).message === "string"
        ) {
          errorDetail = (error.error as { message: string }).message;
        } else {
          errorDetail = error.message ?? String(error.error);
        }
      }
    }

    this.showError(errorMessage, errorDetail);
  }

  /**
   * Clears all messages
   */
  public clear(): void {
    this.messageService.clear();
  }
}
